import { Injectable, signal, inject, computed } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { BnNgIdleService } from 'bn-ng-idle';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { StorageService } from './storage.service';
import { SpinnerService } from './spinner.service';
import { PreferencesService } from './preferences.service';
import { Observable, firstValueFrom, of, throwError, defer, timer, from } from 'rxjs';
import { catchError, delay, filter, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { GarsUserProfile } from '../feature-services/common-service.models';
import { GARS_PERMISSIONS, DataScope, DataScopeLevel } from '../models/gars-permissions.model';
import { CompanyReferencesStore } from './company-references.store';
import { extractApiErrorMessage } from '../shared/utils/error-handler.util';

/** localStorage keys for the gateway's own token pair. */
export const ACCESS_TOKEN_KEY = 'accessToken';
export const ACCESS_TOKEN_REFRESH_KEY = 'refreshToken';

/** Key holding the serialised SSO user profile. */
export const USER_STORAGE_KEY = 'user';

/** localStorage keys for GARS permissions, company scoping, data scope, and profile. */
export const GARS_PERMISSIONS_KEY = 'gars_permissions';
export const GARS_COMPANY_CODES_KEY = 'gars_company_codes';
export const GARS_ACCESS_SCOPES_KEY = 'gars_access_scopes';
export const GARS_DATA_SCOPE_KEY = 'gars_data_scope';
export const GARS_USER_PROFILE_KEY = 'gars_user_profile';

/**
 * Every key this service owns. Used to tear the session down surgically instead
 * of calling localStorage.clear(), which would also delete unrelated keys other
 * parts of the app (or other libraries) keep on this origin.
 */
export const AUTH_STORAGE_KEYS = [
  ACCESS_TOKEN_KEY,
  ACCESS_TOKEN_REFRESH_KEY,
  USER_STORAGE_KEY,
  GARS_PERMISSIONS_KEY,
  GARS_COMPANY_CODES_KEY,
  GARS_ACCESS_SCOPES_KEY,
  GARS_DATA_SCOPE_KEY,
  GARS_USER_PROFILE_KEY,
] as const;

/** The gateway's own tokens, returned by /auth/login and /auth/refresh. */
export interface GarsTokens {
  accessToken: string;
  refreshToken: string;
  tokenType?: string;
  expiresIn?: number;
  permissions?: string[];
  companyCodes?: string[];
  accessScopes?: string[];
}

export interface BackendAuthResponse {
  status: number;
  message: string;
  data: GarsTokens;
}

/**
 * Token lifecycle constants.
 * Google ID tokens are issued for ~1 hour; we silently renew when the current
 * token has less than TOKEN_RENEWAL_THRESHOLD_SECONDS left, requesting a fresh
 * one that stays valid for TOKEN_VALIDITY_SECONDS.
 */
export const TOKEN_RENEWAL_THRESHOLD_SECONDS = 120; // 2 min
export const TOKEN_VALIDITY_SECONDS = 60 * 60; // 60 min

/**
 * UI-only warning level. Much wider than the silent-renew threshold: this is
 * the point where the header turns red and offers the user an explicit renew
 * button, giving them a comfortable window to act before the token actually
 * needs replacing.
 */
export const TOKEN_WARNING_SECONDS = 600; // 10 min

/**
 * Idle handling. The window is not a flat duration: it is this fraction of the
 * token's remaining lifetime, so a refreshed token also grants a longer grace
 * period before the user is warned about being idle.
 */
export const IDLE_LIFETIME_FRACTION = 0.75;

/**
 * How long the "you have been idle" dialog waits for an answer before logging
 * the user out regardless.
 */
export const IDLE_LOGOUT_GRACE_SECONDS = 30;

// #NVGSTKB lgtm
@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly userRole = signal<'User' | 'Admin' | null>(null);
  readonly userProfile = signal<GarsUserProfile | null>(this.getStoredUserProfile());
  readonly permissions = signal<string[]>(this.getStoredPermissions());
  readonly companyCodes = signal<string[]>(this.getStoredCompanyCodes());
  readonly accessScopes = signal<string[]>(this.getStoredAccessScopes());
  readonly dataScope = signal<DataScope>(this.getStoredDataScope());
  readonly effectiveRole = computed<'Super Admin' | 'User'>(() => {
    return (this.isSuperAdmin() || this.userRole() === 'Admin') && this.userRole() !== 'User' ? 'Super Admin' : 'User';
  });
  readonly isGlobalScope = computed<boolean>(() => {
    return this.dataScope().level === 'global' || (this.isSuperAdmin() && this.companyCodes().length === 0);
  });
  readonly allowedCompanyCodes = computed<string[]>(() => {
    const list = this.dataScope().allowedCompanies.length > 0
      ? this.dataScope().allowedCompanies
      : this.companyCodes();
    return list.map(c => (c || '').trim()).filter(Boolean);
  });
  readonly storageService = inject(StorageService);
  readonly spinner = inject(SpinnerService);
  readonly preferencesService = inject(PreferencesService);
  readonly companyStore = inject(CompanyReferencesStore);

  /** Interactive SSO sign-in state and stepped loader stage telemetry */
  readonly isSigningIn = signal<boolean>(false);
  readonly signingInStageText = signal<string>("Authenticating Google Single Sign-On session…");
  readonly signingInStages = [
    "Authenticating Google Single Sign-On session…",
    "Exchanging security tokens with GARS Gateway…",
    "Retrieving user profile and permissions…",
    "Preparing your workspace analytics dashboard…",
  ];

  /** Login error messaging with first-priority backend extracted details */
  readonly loginError = signal<string | null>(null);
  readonly loginErrorDetail = signal<string | null>(null);

  private static instance: AuthService | null = null;

  /** Sets the current login error */
  setLoginError(message: string, detail: string | null = null): void {
    this.loginError.set(message);
    this.loginErrorDetail.set(detail);
  }

  /** Clears the current login error */
  clearLoginError(): void {
    this.loginError.set(null);
    this.loginErrorDetail.set(null);
  }

  /** Static broadcaster so external providers (e.g. Google Login onError) can notify */
  static broadcastLoginError(err: any): void {
    if (AuthService.instance) {
      const msg = extractApiErrorMessage(err, 'Google Sign-In failed or was cancelled.');
      let detail: string | null = null;
      if (typeof err === 'object' && err?.error === 'popup_closed_by_user') {
        detail = 'The Google sign-in window was closed before completing verification.';
      } else if (typeof err === 'object' && err?.error === 'access_denied') {
        detail = 'Access was denied during Google authentication.';
      }
      AuthService.instance.setLoginError(msg, detail);
      AuthService.instance.isSigningIn.set(false);
      AuthService.instance.spinner.setSuppressed(false);
      AuthService.instance.spinner.hide();
    }
  }

  constructor(
    private socialAuthService: SocialAuthService,
    private http: HttpClient,
    private router: Router,
    private idle: BnNgIdleService,
  ) {
    AuthService.instance = this;

    this.socialAuthService.authState.subscribe((user) => {
      if (user && user.idToken) {
        this.clearLoginError();
        this.storeUser(user);
        this.isSigningIn.set(true);
        this.signingInStageText.set("Exchanging security tokens with GARS Gateway…");
        this.spinner.setSuppressed(true);

        // SSO completed on login page:
        // 1. Call login (exchangeToken -> loginWithGoogleToken)
        // 2. Grab JWT (stored in storeTokens)
        // 3. Update stage & call profile API (fetchUserProfile with forceRefresh = true)
        // 4. Navigate dynamically to user's authorized feature dashboard & arm auto-renewal
        this.exchangeToken(user.idToken).pipe(
          tap(() => {
            this.signingInStageText.set("Retrieving user profile and permissions…");
          }),
          delay(500),
          switchMap(() => this.fetchUserProfile(0, true)),
          tap(() => {
            this.signingInStageText.set("Preparing your authorized dashboard…");
            this.scheduleAutoRenew();
          }),
          switchMap(() => from(this.router.navigate([this.getDefaultFeatureRoute()]))),
          delay(400),
          catchError((err) => {
            console.warn('Login or profile load following exchange error:', err);
            const errorMsg = extractApiErrorMessage(err, 'Unable to authenticate with GARS. Please try again.');
            let errorDetail: string | null = null;
            if (err?.status === 401 || err?.status === 403) {
              errorDetail = 'Your Google account may not be provisioned or lacks active permissions in GARS. Please contact your system administrator.';
            } else if (err?.status === 0) {
              errorDetail = 'Unable to connect to GARS Gateway. Please verify your internet or VPN connection.';
            } else if (err?.status >= 500) {
              errorDetail = 'The GARS authentication service encountered an internal error. Please try again later.';
            }
            this.setLoginError(errorMsg, errorDetail);

            if (!this.accessToken()) {
              this.clearSession();
              return of(null);
            }
            this.scheduleAutoRenew();
            this.router.navigate([this.getDefaultFeatureRoute()]);
            return of(null);
          }),
          finalize(() => {
            this.isSigningIn.set(false);
            this.spinner.setSuppressed(false);
            this.spinner.hide();
          })
        ).subscribe();
      } else {
        this.clearSession();
      }
    });

    // A page reload leaves localStorage populated but the service freshly
    // constructed, so arm the renewal on start-up too, not just on sign-in.
    this.scheduleAutoRenew();
    this.startIdleWatch();

    // Hydrate user profile on startup if session already exists.
    // Stale-While-Revalidate pattern:
    // 1. Immediately hydrate signals from cached profile in localStorage for instant render
    // 2. Always dispatch a background API call to verify latest permissions, role, and data scopes
    const storedProfile = this.getStoredUserProfile();
    if (storedProfile) {
      this.profileFetchedSuccessfully = true;
      this.applyProfileData(storedProfile);
    }
    if (this.isLoggedIn()) {
      this.fetchUserProfile(0, true).subscribe();
    }

    // Monitor token expiry on every route switch. Inside the renewal threshold
    // the user is asked to confirm, so a session can never lapse while they are
    // reading a page without navigating.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.renewIfExpiringSoon().catch(() => {
        });
      });
  }

  // ── Session extension consent ──

  private autoRenewTimer: ReturnType<typeof setTimeout> | null = null;
  private autoRenewPoll: ReturnType<typeof setInterval> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * A background tab has its timers throttled to ~1/minute, so a single long
   * setTimeout is not trustworthy. This poll re-checks on a short interval and
   * is the real safety net; the timeout is only there to fire promptly while
   * the tab is in the foreground.
   */
  private readonly AUTO_RENEW_POLL_MS = 15000;

  /**
   * True while the Google account chooser is open, whether it was reached
   * through the user's consent or the header's Renew button. The header uses
   * this to show a spinner so the popup does not appear unexplained.
   */
  readonly autoRenewPending = signal(false);

  /**
   * True once the token is inside TOKEN_RENEWAL_THRESHOLD_SECONDS and the user
   * has not answered yet. The header renders the "extend session?" confirmation
   * from this - the service deliberately does not open any UI of its own.
   */
  readonly sessionExtendPrompt = signal(false);

  /**
   * The user answered "no". Latched so the poll and every route change stop
   * re-asking; the session then simply runs down to 00:00.
   */
  private extendDeclined = false;

  /**
   * Arm the session lifecycle for the current token. Called on construction and
   * again after every successful renewal, so a fresh token always pushes the
   * next prompt out by a full hour.
   */
  private scheduleAutoRenew(): void {
    if (typeof window === 'undefined') return;
    if (this.autoRenewTimer) {
      clearTimeout(this.autoRenewTimer);
      this.autoRenewTimer = null;
    }

    // Deferred: this runs from the constructor, and router.navigate() during
    // service construction races the initial guard check.
    setTimeout(() => this.checkSession(), 0);

    if (this.autoRenewPoll) clearInterval(this.autoRenewPoll);
    this.autoRenewPoll = setInterval(() => this.checkSession(), this.AUTO_RENEW_POLL_MS);
  }

  /**
   * Single decision point for the session, shared by the timeout, the poll and
   * the route-change check: sign out once lapsed, ask for consent inside the
   * renewal window, otherwise sleep until the window opens.
   */
  private checkSession(): void {
    const remaining = this.secondsToExpiry(this.expiringToken() ?? '');
    // Not signed in, or the token cannot be decoded - nothing to manage.
    if (remaining === null) return;

    if (remaining <= 0) {
      this.signOut();
      return;
    }

    if (remaining > TOKEN_RENEWAL_THRESHOLD_SECONDS) {
      // Plenty of time left: sleep until the prompt is actually due.
      if (!this.autoRenewTimer) {
        this.autoRenewTimer = setTimeout(
          () => this.checkSession(),
          (remaining - TOKEN_RENEWAL_THRESHOLD_SECONDS) * 1000,
        );
      }
      return;
    }

    // Inside the window: ask, don't act. The user decides whether to extend.
    this.raiseExtendPrompt();
  }

  // ── Inactivity watch ──

  private idleWatchStarted = false;
  private idleLogoutTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * True while the "you have been idle" dialog is up. The header renders it, and
   * it also blocks the token-expiry prompt so the user is never asked two
   * questions at once.
   */
  readonly idleLogoutPrompt = signal(false);

  /**
   * How long the idle dialog waits for an answer before logging the user out
   * anyway. Ignoring the dialog is treated as "not answering".
   */
  private idleGraceSeconds = IDLE_LOGOUT_GRACE_SECONDS;

  /**
   * The idle window: 75% of whatever is left on the token. Tying it to the
   * token rather than a flat 15 minutes means a freshly refreshed token also
   * grants a fresh, long idle window.
   */
  private idleWindowSeconds(): number | null {
    const remaining = this.secondsToExpiry(this.expiringToken() ?? '');
    if (remaining === null || remaining <= 0) return null;
    return Math.max(IDLE_LOGOUT_GRACE_SECONDS, Math.floor(remaining * IDLE_LIFETIME_FRACTION));
  }

  /**
   * Start watching for inactivity, or re-arm the existing watch with a fresh
   * window. Called on sign-in, on every token change, and whenever the user
   * chooses to stay signed in.
   */
  private startIdleWatch(): void {
    const windowSeconds = this.idleWindowSeconds();
    if (windowSeconds === null) return;

    if (this.idleWatchStarted) {
      // startWatching() would leak a second activity subscription, so an
      // existing watch is re-armed instead of replaced.
      this.idle.resetTimer(windowSeconds);
      return;
    }

    this.idleWatchStarted = true;
    this.idle.startWatching(windowSeconds).subscribe(() => {
      // BnNgIdleService repeats this emission every interval rather than firing
      // once, so the guard below keeps a single dialog up rather than stacking.
      this.raiseIdleLogoutPrompt();
    });
  }

  /** Idle threshold reached: ask first, and log out if nobody answers. */
  private raiseIdleLogoutPrompt(): void {
    if (this.idleLogoutPrompt()) return;
    this.idleLogoutPrompt.set(true);
    this.cancelIdleLogoutTimer();
    this.idleLogoutTimer = setTimeout(() => {
      this.idleLogoutTimer = null;
      this.idleLogoutPrompt.set(false);
      this.signOut();
    }, this.idleGraceSeconds * 1000);
  }

  /** The user pressed "Log out": end the session now. */
  confirmIdleLogout(): void {
    this.cancelIdleLogoutTimer();
    this.idleLogoutPrompt.set(false);
    this.signOut();
  }

  /**
   * The user pressed "Not now" (or dismissed the dialog, which routes through
   * (cancel)): stay signed in and start idle tracking over from the beginning.
   */
  staySignedIn(): void {
    this.cancelIdleLogoutTimer();
    this.idleLogoutPrompt.set(false);
    this.startIdleWatch();
  }

  private cancelIdleLogoutTimer(): void {
    if (!this.idleLogoutTimer) return;
    clearTimeout(this.idleLogoutTimer);
    this.idleLogoutTimer = null;
  }

  private stopIdleWatch(): void {
    this.cancelIdleLogoutTimer();
    this.idleLogoutPrompt.set(false);
    // stopTimer() throws if startWatching() never ran, hence the flag.
    if (!this.idleWatchStarted) return;
    this.idleWatchStarted = false;
    this.idle.stopTimer();
  }

  /** Show the consent prompt once, unless it is already up or already refused. */
  private raiseExtendPrompt(): void {
    // Never stack on top of the idle warning: the user should never be asked
    // two questions at once.
    if (this.idleLogoutPrompt()) return;
    if (this.sessionExtendPrompt() || this.extendDeclined) return;
    if (Date.now() < this.promptRetryAfter) return;
    this.sessionExtendPrompt.set(true);
  }

  /**
   * Earliest time the prompt may be raised again after a failed renewal, and
   * the wall-clock stamp for the current backoff (0 = no backoff).
   */
  private promptRetryAfter = 0;
  private static readonly PROMPT_RETRY_COOLDOWN_MS = 60000;

  /**
   * The user chose to extend: refresh the access token. No Google popup - the
   * stored refresh token is exchanged for a new pair.
   */
  async confirmExtendSession(): Promise<boolean> {
    this.sessionExtendPrompt.set(false);
    this.extendDeclined = false;
    this.cancelExpiryLogout();
    this.autoRenewPending.set(true);
    let renewed = false;
    try {
      renewed = await this.renewSession();
      return renewed;
    } finally {
      this.autoRenewPending.set(false);
      // A failed refresh must not re-ask on the very next 15s poll tick, or the
      // dialog would trap the user in a loop. Back off briefly; the countdown
      // keeps running either way and sign-out still lands at 00:00.
      if (!renewed) this.promptRetryAfter = Date.now() + AuthService.PROMPT_RETRY_COOLDOWN_MS;
      this.checkSession();
    }
  }

  /**
   * The user chose to let the session run out rather than extend it. Stop asking
   * and sign out the moment the token reaches 00:00. This is the "wait" path -
   * the dialog's separate "Sign out now" button ends the session immediately.
   */
  waitForSessionExpiry(): void {
    // Guarded: the dialog also emits (cancel) when it is closed programmatically,
    // so a stray event must not silently start the sign-out countdown.
    if (!this.sessionExtendPrompt()) return;
    this.sessionExtendPrompt.set(false);
    this.extendDeclined = true;

    const remaining = this.secondsToExpiry(this.expiringToken() ?? '');
    if (remaining === null) return;
    this.scheduleExpiryLogout(remaining);
  }

  /**
   * The token whose expiry currently drives the lifecycle: the access token when
   * its `exp` is readable, otherwise the Google ID token.
   */
  private expiringToken(): string | null {
    const access = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (access && this.secondsToExpiry(access) !== null) {
      return access;
    }
    const idToken = this.getStoredUser()?.idToken;
    if (idToken && this.secondsToExpiry(idToken) !== null) {
      return idToken;
    }
    return access || idToken || null;
  }

  /** Sign out the instant the declined token lapses. */
  private scheduleExpiryLogout(remainingSeconds: number): void {
    this.cancelExpiryLogout();
    this.logoutTimer = setTimeout(() => {
      this.logoutTimer = null;
      // Re-check: a manual renewal may have landed while the timer was armed.
      const remaining = this.secondsToExpiry(this.expiringToken() ?? '');
      if (remaining !== null && remaining > 0) {
        this.extendDeclined = false;
        this.checkSession();
        return;
      }
      this.signOut();
    }, Math.max(0, remainingSeconds) * 1000);
  }

  private cancelExpiryLogout(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  // ── Token renewal ──

  /**
   * Ensure the session stays valid across a route change.
   * Returns true when the stored token is either still valid for more than
   * `thresholdSeconds` or was successfully renewed.
   */
  async renewIfExpiringSoon(thresholdSeconds: number = TOKEN_RENEWAL_THRESHOLD_SECONDS): Promise<boolean> {
    // Same token the header countdown reads, so the guard and the timer can
    // never disagree about how long the session has left.
    const token = this.expiringToken();
    if (!token) return false;

    const remaining = this.secondsToExpiry(token);
    // No readable exp claim → let the guards treat it as invalid.
    if (remaining === null) return false;
    // Still far from expiring → nothing to do.
    if (remaining > thresholdSeconds) return true;

    // Inside the window: ask rather than act. The token is still valid, so the
    // guards are satisfied and the user is not bounced to the login page; they
    // simply get the "extend session?" prompt.
    this.raiseExtendPrompt();
    // If a refresh is already in flight from the user's confirmation, share it.
    return this.renewalInFlight ?? false;
  }

  /**
   * The in-flight renewal promise so concurrent callers share one refresh call.
   */
  private renewalInFlight: Promise<boolean> | null = null;

  /**
   * Renew the session by trading the stored refresh token for a new access
   * token - no Google popup. This is the only renewal path, used by the header's
   * Renew button and by confirming the "extend session?" prompt.
   */
  async renewSession(): Promise<boolean> {
    if (!localStorage.getItem(ACCESS_TOKEN_REFRESH_KEY)) return false;
    // Single-flight: the header button, the consent dialog and the interceptor's
    // 401 handler can all trigger a renewal at the same moment.
    if (this.renewalInFlight) return this.renewalInFlight;

    this.renewalInFlight = firstValueFrom(
      this.refreshAccessToken().pipe(
        map((res) => this.storeTokens(res?.data)),
        // A rejected refresh leaves the existing access token in place; the
        // caller decides whether that is fatal.
        catchError(() => of(false)),
      ),
    ).finally(() => {
      this.renewalInFlight = null;
    });

    return this.renewalInFlight;
  }

  // ── Token inspection ──

  /** Decode the JWT payload of a Google ID token. */
  private decodeToken(token: string): any | null {
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
      return JSON.parse(window.atob(padded));
    } catch {
      return null;
    }
  }

  /** Seconds until the token's exp claim, or null when it cannot be read. */
  secondsToExpiry(token: string): number | null {
    const payload = this.decodeToken(token);
    if (!payload || typeof payload.exp !== 'number') return null;
    return payload.exp - Math.floor(Date.now() / 1000);
  }

  /**
   * The gateway access token - the only credential that authorises API calls.
   * Deliberately not the Google ID token: that one proves who the user is to
   * Google, not that the gateway has issued them a live session.
   */
  accessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /**
   * True when the access token is present, decodable and still in the future.
   * An unreadable `exp` is treated as expired rather than valid: an opaque or
   * malformed token cannot be shown to be good, and failing closed means the
   * user re-authenticates instead of being left with a dead session.
   */
  async isTokenExpired(): Promise<boolean> {
    const token = this.expiringToken();
    if (!token) return true;
    const remaining = this.secondsToExpiry(token);
    if (remaining !== null) {
      return remaining <= 0;
    }
    return !this.isLoggedIn();
  }

  /** True when there is a stored user AND the access token is not expired. */
  async isAuthenticated(): Promise<boolean> {
    return this.isLoggedIn() && !(await this.isTokenExpired());
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem(USER_STORAGE_KEY) || !!localStorage.getItem(ACCESS_TOKEN_KEY) || !!localStorage.getItem(GARS_USER_PROFILE_KEY);
  }

  /**
   * Client-side role check for hiding admin-only UI. NOT a security boundary -
   * the gateway must enforce authorisation on every request. Defaults to false
   * so an unrecognised or absent claim hides admin UI rather than exposing it.
   */
  isAdmin(): boolean {
    if (this.userRole() === 'User') return false;
    return this.isSuperAdmin() || this.userRole() === 'Admin';
  }

  // ── GARS Permissions & Company Scope ──

  getStoredPermissions(): string[] {
    try {
      const raw = localStorage.getItem(GARS_PERMISSIONS_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  getStoredCompanyCodes(): string[] {
    try {
      const raw = localStorage.getItem(GARS_COMPANY_CODES_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  getStoredAccessScopes(): string[] {
    try {
      const raw = localStorage.getItem(GARS_ACCESS_SCOPES_KEY);
      return raw ? (JSON.parse(raw) as string[]) : this.getStoredCompanyCodes();
    } catch {
      return [];
    }
  }

  getStoredDataScope(): DataScope {
    try {
      const raw = localStorage.getItem(GARS_DATA_SCOPE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.level) {
          return {
            level: parsed.level,
            allowedRegions: Array.isArray(parsed.allowedRegions) ? parsed.allowedRegions : [],
            allowedCountries: Array.isArray(parsed.allowedCountries) ? parsed.allowedCountries : [],
            allowedCompanies: Array.isArray(parsed.allowedCompanies) ? parsed.allowedCompanies : [],
          };
        }
      }
    } catch {
      // ignore
    }
    const comps = this.getStoredCompanyCodes();
    const perms = this.getStoredPermissions();
    const isSuper = perms.includes(GARS_PERMISSIONS.SUPER_ADMIN);
    return {
      level: isSuper && comps.length === 0 ? 'global' : (comps.length > 0 ? 'company' : (isSuper ? 'global' : 'company')),
      allowedRegions: [],
      allowedCountries: [],
      allowedCompanies: comps,
    };
  }

  /**
   * Reads cached GARS user profile from localStorage if present.
   * Checks dedicated GARS profile key first, then falls back to enriched user object.
   */
  getStoredUserProfile(): GarsUserProfile | null {
    try {
      // 1. Dedicated GARS profile key
      const profileRaw = localStorage.getItem(GARS_USER_PROFILE_KEY) || localStorage.getItem('user_profile');
      if (profileRaw) {
        const parsed = JSON.parse(profileRaw);
        if (parsed && typeof parsed === 'object' && (parsed.email || parsed.holcimId || parsed.fullName || parsed.permissions)) {
          return parsed as GarsUserProfile;
        }
      }

      // 2. Enriched user object in USER_STORAGE_KEY ('user')
      const userRaw = localStorage.getItem(USER_STORAGE_KEY);
      if (userRaw) {
        const parsed = JSON.parse(userRaw);
        if (
          parsed &&
          typeof parsed === 'object' &&
          (parsed.holcimId || parsed.companyCodes || parsed.permissions || parsed.accessScopes || parsed.region || parsed.country)
        ) {
          return parsed as GarsUserProfile;
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Extract permission code strings from profile API response.
   * Handles string arrays, permissionCodes arrays, and object arrays.
   */
  extractPermissionsFromProfile(profile: any): string[] {
    if (!profile) return [];
    const candidates = [
      profile.permissions,
      profile.permissionCodes,
      profile.permission_codes,
      profile.userPermissions,
      profile.user_permissions,
    ];

    for (const cand of candidates) {
      if (Array.isArray(cand) && cand.length > 0) {
        const extracted = cand
          .map((item: any) => {
            if (typeof item === 'string') return item.trim();
            if (typeof item === 'number') return String(item);
            if (item && typeof item === 'object') {
              return (
                item.permissionCode ||
                item.permission_code ||
                item.code ||
                item.name ||
                item.permissionName ||
                item.permission_name ||
                (item.permissionId != null ? String(item.permissionId) : '') ||
                (item.permission_id != null ? String(item.permission_id) : '') ||
                ''
              );
            }
            return '';
          })
          .filter(Boolean);
        if (extracted.length > 0) {
          return [...new Set(extracted)];
        }
      }
    }
    return [];
  }

  /**
   * Extract operating company codes from profile API response.
   * Defensively searches companyCodes, company_codes, allowedCompanies, companies, and single companyCode.
   */
  extractCompanyCodesFromProfile(profile: any): string[] {
    if (!profile) return [];
    const candidates = [
      profile.accessScopes,
      profile.access_scopes,
      profile.companyCodes,
      profile.company_codes,
      profile.allowedCompanies,
      profile.companies,
    ];

    for (const cand of candidates) {
      if (Array.isArray(cand) && cand.length > 0) {
        const extracted = cand
          .map((item: any) => {
            if (typeof item === 'string') return item.trim();
            if (item && typeof item === 'object') {
              return item.companyCode || item.company_code || item.code || '';
            }
            return '';
          })
          .filter(Boolean);
        if (extracted.length > 0) {
          return [...new Set(extracted)];
        }
      }
    }

    if (typeof profile.companyCode === 'string' && profile.companyCode.trim()) {
      return [profile.companyCode.trim()];
    }
    if (typeof profile.company_code === 'string' && profile.company_code.trim()) {
      return [profile.company_code.trim()];
    }

    const userPerms = profile.userPermissions || profile.user_permissions;
    if (Array.isArray(userPerms)) {
      const fromPerms = userPerms
        .map((up: any) => up?.companyCode || up?.company_code)
        .filter((c: any) => typeof c === 'string' && c.trim());
      if (fromPerms.length > 0) {
        return [...new Set(fromPerms)];
      }
    }

    return [];
  }

  /**
   * Computes DataScope dynamically evaluating authorized company codes.
   * Leverages CompanyReferencesStore.inferDataScopeFromCompanyCodes to intelligently determine
   * whether the scope is Region-Based, Country-Based, Company-Based, or Global based on complete set evaluation.
   */
  computeDataScope(profile: GarsUserProfile | null, companyCodes: string[]): DataScope {
    const perms = this.permissions();
    const isSuper = perms.includes(GARS_PERMISSIONS.SUPER_ADMIN) ||
                    (profile?.role && String(profile.role).toLowerCase().includes('admin'));

    const profileScopeLevel = (typeof profile?.dataScope === 'string'
      ? (profile.dataScope.toLowerCase() as DataScopeLevel)
      : (profile?.dataScope as any)?.level) || undefined;

    // When companyCodes are present, evaluate the complete set against region/country hierarchy
    if (companyCodes && companyCodes.length > 0) {
      return this.companyStore.inferDataScopeFromCompanyCodes(
        companyCodes,
        isSuper ? 'Super Admin' : (profile?.role || 'User'),
        {
          region: profile?.region,
          country: profile?.country,
          dataScopeLevel: profileScopeLevel,
        }
      );
    }

    // Check if profile explicitly provided an object for dataScope
    if (profile?.dataScope && typeof profile.dataScope === 'object') {
      const ds = profile.dataScope as any;
      if (ds.allowedCompanies && ds.allowedCompanies.length > 0) {
        return this.companyStore.inferDataScopeFromCompanyCodes(
          ds.allowedCompanies,
          isSuper ? 'Super Admin' : (profile?.role || 'User'),
          {
            region: profile?.region,
            country: profile?.country,
            dataScopeLevel: ds.level,
          }
        );
      }
      return {
        level: ds.level || (isSuper ? 'global' : 'company'),
        allowedRegions: Array.isArray(ds.allowedRegions) ? ds.allowedRegions : (profile?.region ? [profile.region] : []),
        allowedCountries: Array.isArray(ds.allowedCountries) ? ds.allowedCountries : (profile?.country ? [profile.country] : []),
        allowedCompanies: Array.isArray(ds.allowedCompanies) ? ds.allowedCompanies : [],
      };
    }

    // Check if profile provided string level for dataScope
    if (profileScopeLevel && ['global', 'region', 'country', 'company'].includes(profileScopeLevel)) {
      return {
        level: profileScopeLevel,
        allowedRegions: profile?.region ? [profile.region] : [],
        allowedCountries: profile?.country ? [profile.country] : [],
        allowedCompanies: [],
      };
    }

    if (isSuper) {
      return {
        level: 'global',
        allowedRegions: [],
        allowedCountries: [],
        allowedCompanies: [],
      };
    }

    if (profile?.country) {
      return {
        level: 'country',
        allowedRegions: profile?.region ? [profile.region] : [],
        allowedCountries: [profile.country],
        allowedCompanies: [],
      };
    }

    if (profile?.region) {
      return {
        level: 'region',
        allowedRegions: [profile.region],
        allowedCountries: [],
        allowedCompanies: [],
      };
    }

    return {
      level: 'company',
      allowedRegions: [],
      allowedCountries: [],
      allowedCompanies: [],
    };
  }

  /**
   * Applies and persists profile data, permissions, companyCodes, and dataScope.
   */
  applyProfileData(profile: GarsUserProfile): void {
    if (!profile) return;
    this.userProfile.set(profile);
    localStorage.setItem(GARS_USER_PROFILE_KEY, JSON.stringify(profile));
    if (profile.email || (profile as any).userName) {
      this.preferencesService.setUser(profile.email || (profile as any).userName);
    }

    // 1. Set Permissions from Profile API
    const extractedPerms = this.extractPermissionsFromProfile(profile);
    if (extractedPerms.length > 0) {
      localStorage.setItem(GARS_PERMISSIONS_KEY, JSON.stringify(extractedPerms));
      this.permissions.set(extractedPerms);
      if (extractedPerms.includes(GARS_PERMISSIONS.SUPER_ADMIN)) {
        this.userRole.set('Admin');
      }
    } else if (profile.role) {
      const r = String(profile.role).toLowerCase();
      if (r.includes('admin')) {
        this.userRole.set('Admin');
      } else {
        this.userRole.set('User');
      }
    }

    // 2. Set Company Codes & Access Scopes from Profile API
    const extractedCompanyCodes = this.extractCompanyCodesFromProfile(profile);
    if (extractedCompanyCodes.length > 0) {
      localStorage.setItem(GARS_COMPANY_CODES_KEY, JSON.stringify(extractedCompanyCodes));
      localStorage.setItem(GARS_ACCESS_SCOPES_KEY, JSON.stringify(extractedCompanyCodes));
      this.companyCodes.set(extractedCompanyCodes);
      this.accessScopes.set(extractedCompanyCodes);
    }

    // 3. Set Data Scope referring companyCodes
    const effectiveComps = extractedCompanyCodes.length > 0 ? extractedCompanyCodes : this.companyCodes();
    const computedScope = this.computeDataScope(profile, effectiveComps);
    localStorage.setItem(GARS_DATA_SCOPE_KEY, JSON.stringify(computedScope));
    this.dataScope.set(computedScope);

    // 4. Merge with stored user in localStorage
    const stored = this.getStoredUser() || ({} as any);
    const merged = {
      ...stored,
      ...profile,
      companyCodes: effectiveComps,
      accessScopes: effectiveComps,
      dataScope: computedScope,
      permissions: this.permissions(),
      firstName: profile.firstName || (stored as any).firstName || (profile.fullName ? profile.fullName.split(' ')[0] : ''),
      lastName: profile.lastName || (stored as any).lastName || (profile.fullName ? profile.fullName.split(' ').slice(1).join(' ') : ''),
      photoUrl: profile.photoUrl || profile.avatar || (stored as any).photoUrl,
      email: profile.email || (stored as any).email,
    };
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(merged));
  }

  isSuperAdmin(): boolean {
    if (this.userRole() === 'User') return false;
    const raw = this.permissions();
    return raw.some(p => {
      const s = String(p).trim().toUpperCase();
      return s === GARS_PERMISSIONS.SUPER_ADMIN || s === '1' || s === 'USAGE_ALL_READ' || s === 'SUPER ADMIN';
    });
  }

  hasPermission(permission: string | number): boolean {
    if (!permission) return true;
    if (this.isSuperAdmin()) return true;

    const target = String(permission).trim().toUpperCase();
    const raw = this.permissions().map(p => String(p).trim().toUpperCase());
    if (raw.includes(target)) return true;

    const permissionEquivalents: Record<string, (string | number)[]> = {
      [GARS_PERMISSIONS.SUPER_ADMIN]: ['SUPER_ADMIN', '1', 1, 'USAGE_ALL_READ', 'GEMINI_USAGE_ALL_READ', 'SUPER ADMIN'],
      [GARS_PERMISSIONS.WORKSPACE_ADOPTION_VIEW]: ['WORKSPACE_ADOPTION_VIEW', 'WORKSPACE_ANALYTICS_SEARCH', '2', 2, 'WORKSPACE_SEARCH', 'WORKSPACE ANALYTICS USER SEARCH', 'WORKSPACE_ANALYTICS_VIEW', 'WORKSPACE_ANALYTICS', 'DASHBOARD', 'VIEW WORKSPACE ADOPTION'],
      [GARS_PERMISSIONS.WORKSPACE_ADOPTION_EXTERNAL_EMAIL_SEARCH]: ['WORKSPACE_ADOPTION_EXTERNAL_EMAIL_SEARCH', 'EXTERNAL SEARCH BY EMAIL', 'EXTERNAL_EMAIL_SEARCH', 'USER EMAIL SEARCH'],
      [GARS_PERMISSIONS.WORKSPACE_ADOPTION_REPORT_EXPORT]: ['WORKSPACE_ADOPTION_REPORT_EXPORT', 'EXPORT ADOPTION REPORT', 'WORKSPACE_EXPORT', 'DOWNLOAD REPORTS', 'REPORT_EXPORT'],
      [GARS_PERMISSIONS.WORKSPACE_ANALYTICS_SEARCH]: ['WORKSPACE_ANALYTICS_SEARCH', 'WORKSPACE_ADOPTION_VIEW', '2', 2, 'WORKSPACE_SEARCH', 'WORKSPACE ANALYTICS USER SEARCH', 'WORKSPACE_ANALYTICS_VIEW', 'WORKSPACE_ANALYTICS', 'DASHBOARD'],
      [GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW]: ['GEMINI_OVERVIEW_VIEW', '3', 3, 'GEMINI_USAGE_COMPANY_SCOPED_READ', 'USAGE_COMPANY_SCOPED_READ', 'GEMINI OVERVIEW & BENCHMARKING', 'GEMINI_REPORTS'],
      [GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW]: ['GEMINI_TELEMETRY_VIEW', '4', 4, 'GEMINI_USAGE_TEAM_READ', 'USAGE_TEAM_READ', 'GEMINI EMPLOYEE TELEMETRY'],
      [GARS_PERMISSIONS.LICENSE_OVERVIEW_VIEW]: ['LICENSE_OVERVIEW_VIEW', '5', 5, 'LICENSE OVERVIEW', 'LICENSE_MANAGEMENT'],
      [GARS_PERMISSIONS.LICENSE_DIRECTORY_VIEW]: ['LICENSE_DIRECTORY_VIEW', '6', 6, 'EMPLOYEE LICENSE DIRECTORY', 'LICENSE_DIRECTORY'],
      [GARS_PERMISSIONS.ACCESS_SECURITY_MANAGE]: ['ACCESS_SECURITY_MANAGE', '7', 7, 'USER_MANAGE', 'GEMINI_USER_MANAGE', 'ACCESS & SECURITY MANAGEMENT', 'SETTINGS'],
    };

    for (const [canonical, aliases] of Object.entries(permissionEquivalents)) {
      const allEq = [canonical.toUpperCase(), ...aliases.map(a => String(a).toUpperCase())];
      if (allEq.includes(target)) {
        if (allEq.some(eq => raw.includes(eq))) {
          return true;
        }
      }
    }

    return false;
  }

  hasAnyPermission(permissions: (string | number)[]): boolean {
    if (!permissions || permissions.length === 0) return true;
    if (this.isSuperAdmin()) return true;
    return permissions.some((p) => this.hasPermission(p));
  }

  /**
   * Dynamically updates the current active session's permissions and company scopes.
   * Immediately re-computes signals to update sidebar and guards in real-time.
   */
  updateCurrentSessionPermissions(newPermissions: (string | number)[], allowedCompanies?: string[], role?: 'Super Admin' | 'User' | string): void {
    const cleanPerms = newPermissions.map(p => String(p).trim()).filter(Boolean);
    localStorage.setItem(GARS_PERMISSIONS_KEY, JSON.stringify(cleanPerms));
    this.permissions.set(cleanPerms);

    if (allowedCompanies !== undefined) {
      const cleanComps = allowedCompanies.map(c => String(c).trim()).filter(Boolean);
      localStorage.setItem(GARS_COMPANY_CODES_KEY, JSON.stringify(cleanComps));
      localStorage.setItem(GARS_ACCESS_SCOPES_KEY, JSON.stringify(cleanComps));
      this.companyCodes.set(cleanComps);
      this.accessScopes.set(cleanComps);
      const computedScope = this.computeDataScope(this.userProfile(), cleanComps);
      localStorage.setItem(GARS_DATA_SCOPE_KEY, JSON.stringify(computedScope));
      this.dataScope.set(computedScope);
    }

    const isSuper = role !== undefined
      ? (role === 'Super Admin' || role === 'Admin')
      : (cleanPerms.includes(GARS_PERMISSIONS.SUPER_ADMIN) || cleanPerms.includes('1') || cleanPerms.includes('SUPER ADMIN') || cleanPerms.includes('USAGE_ALL_READ'));

    this.userRole.set(isSuper ? 'Admin' : 'User');
    const prof = this.userProfile();
    if (prof) {
      this.userProfile.set({
        ...prof,
        role: isSuper ? 'Super Admin' : 'User',
      });
    }
  }

  /**
   * Evaluates if the current session user has access to a given functional module.
   */
  hasModuleAccess(moduleId: string): boolean {
    if (!moduleId) return true;
    if (this.isSuperAdmin()) return true;

    const norm = moduleId.toLowerCase().trim();
    switch (norm) {
      case 'dashboard':
      case 'workspace-analytics':
      case 'google-workspace-adoption':
        return (
          this.hasPermission(GARS_PERMISSIONS.WORKSPACE_ADOPTION_VIEW) ||
          this.hasPermission(GARS_PERMISSIONS.WORKSPACE_ANALYTICS_SEARCH) ||
          this.hasPermission('WORKSPACE_ANALYTICS_VIEW') ||
          this.hasPermission('WORKSPACE_ANALYTICS') ||
          this.hasPermission('DASHBOARD') ||
          this.hasPermission('2')
        );

      case 'gemini-reports':
      case 'gemini-intelligence':
      case 'gem-reports':
      case 'gemini-adoption':
      case 'gemini-monitoring':
        return this.hasAnyPermission([
          GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW,
          GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW,
          'GEMINI_REPORTS',
          '3',
          '4',
        ]);

      case 'license-monitoring':
      case 'license-management':
      case 'license-monitoring-dashboard':
        return this.hasAnyPermission([
          GARS_PERMISSIONS.LICENSE_OVERVIEW_VIEW,
          GARS_PERMISSIONS.LICENSE_DIRECTORY_VIEW,
          'LICENSE_MANAGEMENT',
          '5',
          '6',
        ]);

      case 'ai-availability':
      case 'feature-matrix':
      case 'ai-availability-matrix':
        // AI Availability Matrix is open for all authenticated users - no permissions needed
        return true;

      case 'settings':
      case 'access-security':
      case 'role-access':
      case 'admin-settings':
        return (
          this.hasPermission(GARS_PERMISSIONS.ACCESS_SECURITY_MANAGE) ||
          this.isAdmin() ||
          this.hasPermission('7')
        );

      default:
        return true;
    }
  }

  /**
   * Dynamically determines the primary authorized landing feature route for the current session.
   * Eliminates hardcoded navigation to Workspace Adoption so users without workspace permissions
   * land directly on their permitted feature without permission violation prompts.
   */
  getDefaultFeatureRoute(): string {
    if (this.isSuperAdmin()) {
      return '/app/workspace-analytics';
    }

    const featureCandidates: Array<{
      route: string;
      moduleId: string;
      requiredPermission?: string;
      requiredPermissions?: string[];
    }> = [
      {
        route: '/app/workspace-analytics',
        moduleId: 'dashboard',
        requiredPermission: GARS_PERMISSIONS.WORKSPACE_ADOPTION_VIEW,
      },
      {
        route: '/app/gemini-intelligence',
        moduleId: 'gemini-reports',
        requiredPermissions: [
          GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW,
          GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW,
        ],
      },
      {
        route: '/app/license-management',
        moduleId: 'license-monitoring',
        requiredPermissions: [
          GARS_PERMISSIONS.LICENSE_OVERVIEW_VIEW,
          GARS_PERMISSIONS.LICENSE_DIRECTORY_VIEW,
        ],
      },
      {
        route: '/app/access-security',
        moduleId: 'settings',
        requiredPermission: GARS_PERMISSIONS.ACCESS_SECURITY_MANAGE,
      },
      {
        route: '/app/feature-matrix',
        moduleId: 'ai-availability',
        // AI Availability Matrix is open for all authenticated users - no requiredPermission
      },
    ];

    for (const candidate of featureCandidates) {
      if (!this.hasModuleAccess(candidate.moduleId)) {
        continue;
      }
      if (candidate.requiredPermission && !this.hasPermission(candidate.requiredPermission)) {
        continue;
      }
      if (candidate.requiredPermissions && !this.hasAnyPermission(candidate.requiredPermissions)) {
        continue;
      }
      return candidate.route;
    }

    // Default fallback if user has no assigned module (AI Matrix is open for all users)
    return '/app/feature-matrix';
  }

  /**
   * Returns the relative path of the default feature for router redirection.
   */
  getDefaultFeaturePath(): string {
    const route = this.getDefaultFeatureRoute();
    return route.replace(/^\/app\//, '');
  }

  isCompanyAllowed(companyCode: string): boolean {
    if (!companyCode) return false;
    // SUPER_ADMIN has unrestricted access across all companies and regions if global
    if (this.isSuperAdmin() && this.dataScope().level === 'global') return true;
    const target = companyCode.trim().toUpperCase();
    return this.allowedCompanyCodes().some(c => (c || '').trim().toUpperCase() === target);
  }

  canSearchWorkspaceUsers(): boolean {
    return this.canSearchWorkspaceExternalEmail();
  }

  /**
   * View Workspace Adoption (Parent Permission).
   * Allows users to access the Workspace Adoption module, dashboards, and metrics.
   */
  canViewWorkspaceAdoption(): boolean {
    return (
      this.hasPermission(GARS_PERMISSIONS.WORKSPACE_ADOPTION_VIEW) ||
      this.hasPermission(GARS_PERMISSIONS.WORKSPACE_ANALYTICS_SEARCH) ||
      this.hasPermission('WORKSPACE_ANALYTICS_VIEW') ||
      this.isSuperAdmin()
    );
  }

  /**
   * External Search by Email (Additional Permission).
   * Allows searching for users outside current workspace by email address.
   * Can ONLY be granted/active when View Workspace Adoption is enabled.
   */
  canSearchWorkspaceExternalEmail(): boolean {
    if (this.isSuperAdmin()) return true;
    if (!this.canViewWorkspaceAdoption()) return false;
    return this.hasPermission(GARS_PERMISSIONS.WORKSPACE_ADOPTION_EXTERNAL_EMAIL_SEARCH);
  }

  /**
   * Export Adoption Report (Additional Permission).
   * Allows exporting Workspace Adoption reports to PDF and downloading data.
   * Can ONLY be granted/active when View Workspace Adoption is enabled.
   */
  canExportWorkspaceReport(): boolean {
    if (this.isSuperAdmin()) return true;
    if (!this.canViewWorkspaceAdoption()) return false;
    return this.hasPermission(GARS_PERMISSIONS.WORKSPACE_ADOPTION_REPORT_EXPORT);
  }

  canViewGeminiOverview(): boolean {
    return this.hasPermission(GARS_PERMISSIONS.GEMINI_OVERVIEW_VIEW) || this.isSuperAdmin();
  }

  canViewGeminiTelemetry(): boolean {
    return this.hasPermission(GARS_PERMISSIONS.GEMINI_TELEMETRY_VIEW) || this.isSuperAdmin();
  }

  canViewLicenseOverview(): boolean {
    return this.hasPermission(GARS_PERMISSIONS.LICENSE_OVERVIEW_VIEW) || this.isSuperAdmin();
  }

  canViewLicenseDirectory(): boolean {
    return this.hasPermission(GARS_PERMISSIONS.LICENSE_DIRECTORY_VIEW) || this.isSuperAdmin();
  }

  canManageAccessSecurity(): boolean {
    return this.hasPermission(GARS_PERMISSIONS.ACCESS_SECURITY_MANAGE) || this.isSuperAdmin() || this.isAdmin();
  }

  canViewAiAvailability(): boolean {
    // AI Availability Matrix is open for all authenticated users - no permissions required
    return true;
  }

  canExport(): boolean {
    return this.hasPermission(GARS_PERMISSIONS.USAGE_EXPORT) || this.canExportWorkspaceReport() || this.isSuperAdmin();
  }

  canManageUsers(): boolean {
    return this.canManageAccessSecurity();
  }

  // ── Session persistence (compatible with auth.interceptor + header) ──

  getStoredUser(): SocialUser | null {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as SocialUser) : null;
    } catch {
      return null;
    }
  }

  private storeUser(user: SocialUser): void {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    // Signal that the stored token changed so the header countdown can re-read
    // it immediately rather than waiting for its next 1s tick.
    this.tokenRevision.update(v => v + 1);
    // A new token means a new idle budget, measured from 75% of its new life.
    this.startIdleWatch();
  }

  /**
   * Bumped on every write to the stored session. The header watches it to resync
   * the moment a renewal lands, so a new token's `exp` is reflected at once.
   */
  readonly tokenRevision = signal(0);

  /** Seconds left on the token currently in localStorage, or null if unreadable. */
  refreshExpiry(): number | null {
    // ONLY the gateway access token. The Google ID token must not be used here:
    // it is written the moment SSO returns, while /auth/login is still pending,
    // which made the header count down from ~59:59 before the real token
    // existed and then jump once the login response arrived. Returning null
    // until /auth/login stores an access token means the clock simply stays
    // hidden while the session is being established.
    const access = localStorage.getItem(ACCESS_TOKEN_KEY);
    return access ? this.secondsToExpiry(access) : null;
  }

  /**
   * Exchange a Google ID token for the gateway's own token pair:
   *   POST /api/v1/auth/login   Authorization: Bearer <google-token>
   *   → { status, message, data: { accessToken, refreshToken } }
   *
   * These GARS tokens - not the Google ID token - authorise every later API
   * call, which is sent as X-Gars-Authorization (see authInterceptor).
   */
  loginWithGoogleToken(idToken: string): Observable<BackendAuthResponse> {
    return this.http.post<BackendAuthResponse>(
      `${environment.apiUrl}v1/auth/login`,
      {},
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
  }

  private refreshInFlight$: Observable<BackendAuthResponse> | null = null;

  /**
   * Mint a new access token from the stored refresh token:
   *   POST /api/v1/auth/refresh  { refreshToken }
   *   → { status, message, data: { accessToken, refreshToken } }
   * Deduplicated so concurrent calls share a single request.
   */
  refreshAccessToken(): Observable<BackendAuthResponse> {
    const refreshToken = localStorage.getItem(ACCESS_TOKEN_REFRESH_KEY);
    if (!refreshToken) return throwError(() => new Error('No refresh token stored'));

    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.http.post<BackendAuthResponse>(
      `${environment.apiUrl}v1/auth/refresh`,
      { refreshToken },
    ).pipe(
      tap((res) => {
        if (res?.data) {
          this.storeTokens(res.data);
        }
      }),
      shareReplay(1),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
    );

    return this.refreshInFlight$;
  }

  /** Persist a token pair and republish it so dependents can react. */
  private storeTokens(data: GarsTokens | null | undefined): boolean {
    const token = data?.accessToken || (data as any)?.token || (data as any)?.access_token;
    if (!token) return false;
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    // A rotated refresh token is honoured when present; otherwise keep the
    // existing one, since not every gateway rotates it.
    const refreshToken = data?.refreshToken || (data as any)?.refresh_token;
    if (refreshToken) localStorage.setItem(ACCESS_TOKEN_REFRESH_KEY, refreshToken);

    // Store permissions returned by GARS login if present
    const perms = data?.permissions || (data as any)?.permission_codes || (data as any)?.user_permissions;
    if (Array.isArray(perms)) {
      const cleanPerms = perms.map((p: any) => typeof p === 'string' ? p.trim() : (p?.permissionCode || p?.code || '')).filter(Boolean);
      if (cleanPerms.length > 0) {
        localStorage.setItem(GARS_PERMISSIONS_KEY, JSON.stringify(cleanPerms));
        this.permissions.set(cleanPerms);
        if (cleanPerms.includes(GARS_PERMISSIONS.SUPER_ADMIN)) {
          this.userRole.set('Admin');
        }
      }
    }

    // Store companyCodes and accessScopes returned by GARS login if present
    const comps = data?.accessScopes || (data as any)?.access_scopes || data?.companyCodes || (data as any)?.company_codes || (data as any)?.companies;
    if (Array.isArray(comps)) {
      const cleanComps = comps.map((c: any) => typeof c === 'string' ? c.trim() : (c?.companyCode || c?.code || '')).filter(Boolean);
      if (cleanComps.length > 0) {
        localStorage.setItem(GARS_COMPANY_CODES_KEY, JSON.stringify(cleanComps));
        localStorage.setItem(GARS_ACCESS_SCOPES_KEY, JSON.stringify(cleanComps));
        this.companyCodes.set(cleanComps);
        this.accessScopes.set(cleanComps);
      }
    }

    // Initial dataScope from login token referring companyCodes
    const initScope = this.computeDataScope(this.userProfile(), this.companyCodes());
    localStorage.setItem(GARS_DATA_SCOPE_KEY, JSON.stringify(initScope));
    this.dataScope.set(initScope);

    this.accessTokenRevision.update(v => v + 1);
    this.applyRoleFromToken(token);
    // The idle budget is 75% of the token's life, so a new access token has to
    // re-arm the idle watch as well.
    this.startIdleWatch();
    return true;
  }

  /** Bumped whenever the gateway access token changes. */
  readonly accessTokenRevision = signal(0);

  /**
   * Derive the UI role from the access token's own claims.
   *
   * This only drives which admin navigation items and panels are rendered - it
   * is NOT an authorisation check, because anything in the browser can be
   * tampered with. The gateway must reject unauthorised requests regardless of
   * what this returns.
   *
   * Unknown, missing or malformed claims fall back to 'User' so admin UI is
   * hidden rather than exposed.
   */
  private applyRoleFromToken(accessToken: string): void {
    const payload = this.decodeToken(accessToken);
    const raw = payload?.role ?? payload?.roles?.[0] ?? payload?.['https://gateway/role'];
    const normalised = Array.isArray(raw) ? raw[0] : raw;
    const role = typeof normalised === 'string' ? normalised.trim().toLowerCase() : '';

    if (role === 'admin' || role === 'super admin' || role === 'superadmin' || this.isSuperAdmin()) {
      this.userRole.set('Admin');
    } else if (role === 'user' || role) {
      this.userRole.set(this.isSuperAdmin() ? 'Admin' : 'User');
    } else {
      this.userRole.set(this.isSuperAdmin() ? 'Admin' : null);
    }
  }

  /** Run the login exchange for a Google ID token and store the result. */
  exchangeToken(idToken: string): Observable<BackendAuthResponse> {
    return this.loginWithGoogleToken(idToken).pipe(
      tap((res: any) => {
        // Grab JWT: save accessToken & refreshToken
        const tokenData = res?.data || res;
        this.storeTokens(tokenData);
      }),
      catchError((err) => {
        console.warn('Login token exchange failed:', err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Local development token helper:
   *   POST /api/v1/auth/local-token?email=<email>
   */
  loginWithLocalToken(email: string): Observable<BackendAuthResponse> {
    return this.http.post<BackendAuthResponse>(
      `${environment.apiUrl}${environment.version}/auth/local-token?email=${encodeURIComponent(email)}`,
      {}
    ).pipe(
      tap((res) => {
        if (res?.data) {
          this.storeTokens(res.data);
        }
      })
    );
  }

  private profileFetchInFlight$: Observable<GarsUserProfile | null> | null = null;
  private profileFetchedSuccessfully = false;

  /**
   * Fetches the user profile from /auth/profile (falling back to /gars-users/profile),
   * enriches the local session and reactive userProfile signal, and updates stored user info.
   * If profile data already exists in localStorage or memory, returns cached profile immediately
   * without calling the API again and again (unless forceRefresh is true).
   */
  fetchUserProfile(delayMs: number = 0, forceRefresh: boolean = true): Observable<GarsUserProfile | null> {
    if (!forceRefresh) {
      const cached = this.getStoredUserProfile() || this.userProfile();
      if (cached) {
        this.profileFetchedSuccessfully = true;
        if (!this.userProfile()) {
          this.applyProfileData(cached);
        }
        return of(cached);
      }
    }

    if (this.profileFetchInFlight$) {
      return this.profileFetchInFlight$;
    }

    const authProfileUrl = `${environment.apiUrl}${environment.version}/auth/profile`;
    const fallbackProfileUrl = `${environment.apiUrl}${environment.version}/gars-users/profile`;

    const request$ = defer(() => {
      return this.http.get<any>(authProfileUrl).pipe(
        catchError(() => this.http.get<any>(fallbackProfileUrl)),
        map((res) => {
          const profile = (res && typeof res === 'object' && 'data' in res ? res.data : res) as GarsUserProfile;
          if (profile) {
            this.profileFetchedSuccessfully = true;
            this.applyProfileData(profile);
          }
          return profile;
        }),
        catchError((err) => {
          console.warn('Could not load GARS user profile:', err?.status || err?.message);
          return of(null);
        }),
      );
    });

    const hasToken = !!localStorage.getItem(ACCESS_TOKEN_KEY);
    const effectiveDelay = delayMs > 0 ? delayMs : (!hasToken ? 300 : 0);

    const stream$ = effectiveDelay > 0 ? timer(effectiveDelay).pipe(switchMap(() => request$)) : request$;

    this.profileFetchInFlight$ = stream$.pipe(
      finalize(() => {
        this.profileFetchInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this.profileFetchInFlight$;
  }

  signOut(): void {
    this.clearLoginError();
    this.socialAuthService.signOut().catch(() => { });
    this.clearSession();
    this.router.navigate(['/login']);
  }

  private clearSession(): void {
    // Only the keys this service owns, so unrelated origin data survives a
    // sign-out. Wiping the whole store would also destroy unrelated keys.
    // NOTE: User preferences, search caches, and theme modes are intentionally preserved
    // and NEVER removed on logout.
    for (const key of AUTH_STORAGE_KEYS) localStorage.removeItem(key);
    this.userRole.set(null);
    this.userProfile.set(null);
    this.permissions.set([]);
    this.companyCodes.set([]);
    this.accessScopes.set([]);
    this.dataScope.set({
      level: 'company',
      allowedRegions: [],
      allowedCountries: [],
      allowedCompanies: [],
    });
    this.refreshInFlight$ = null;
    this.profileFetchInFlight$ = null;
    this.profileFetchedSuccessfully = false;
    // No token to manage any more; drop every scheduled callback so nothing
    // fires against a session that no longer exists.
    this.stopIdleWatch();
    if (this.autoRenewPoll) {
      clearInterval(this.autoRenewPoll);
      this.autoRenewPoll = null;
    }
    if (this.autoRenewTimer) {
      clearTimeout(this.autoRenewTimer);
      this.autoRenewTimer = null;
    }
    this.cancelExpiryLogout();
    this.sessionExtendPrompt.set(false);
    this.autoRenewPending.set(false);
    this.isSigningIn.set(false);
    this.spinner.setSuppressed(false);
    this.extendDeclined = false;
    this.promptRetryAfter = 0;
  }
}